import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import InvoiceLayout from "src/pages/invoice-master/invoice-layout.component";
import { Button, Spinner } from "src/components";
import { useToast } from "src/hooks/useToast";
import { getBatchPoList, getPoGeneration } from "src/services/po-api";
import { RootState } from "src/context/store";

const PoBulk = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { poBatchId } = useParams();
  const { poDetails, poFilters } = useSelector((state: RootState) => state.po);

  const [isFetchingPo, setIsFetchingPo] = useState(false);
  const [poList, setPoList] = useState<any[]>([]);
  const [loadingPoIds, setLoadingPoIds] = useState<Set<string | number>>(new Set());
  const [disabledPoIds, setDisabledPoIds] = useState<Set<string | number>>(new Set());
  const [isBulk, setIsBulk] = useState(false);

  const [jsonModal, setJsonModal] = useState<{ show: boolean; data?: any; filename?: string }>({
    show: false,
  });

  /** ----------------------------------------------
   * 🔹 Fetch PO List (Memoized + Error handled)
   * --------------------------------------------- */
  const fetchPoList = useCallback(async () => {
    if (!poBatchId) return;
    setIsFetchingPo(true);

    try {
      const response = await getBatchPoList({
        po_batch_id: poBatchId,
        company_code: poFilters?.company?.code,
      });
      setPoList(response?.data?.data ?? []);
    } catch (error) {
      console.error("Failed to fetch PO list:", error);
      toast({ description: "Failed to fetch PO list", variant: "destructive" });
    } finally {
      setIsFetchingPo(false);
    }
  }, [poBatchId, poFilters?.company?.code, toast]);

  useEffect(() => {
    fetchPoList();
  }, [fetchPoList]);

  /** ----------------------------------------------
   * 🔹 Utility: Loading/Disable State Management
   * --------------------------------------------- */
  const toggleLoading = useCallback((poId: string | number, isLoading: boolean) => {
    setLoadingPoIds((prev) => {
      const updated = new Set(prev);
      isLoading ? updated.add(poId) : updated.delete(poId);
      return updated;
    });

    if (!isLoading) {
      setDisabledPoIds((prev) => {
        const updated = new Set(prev);
        updated.add(poId);
        return updated;
      });
    }
  }, []);

  /** ----------------------------------------------
   * 🔹 Bulk or Single Upload
   * --------------------------------------------- */
  const bulkUpload = useCallback(
    async (items: any[] | any) => {
      if (poFilters?.company?.code !== 239) {
        return toast({
          description: "This feature is not enabled for this company.",
          variant: "destructive",
        });
      }

      const itemList = Array.isArray(items) ? items : [items];
      setIsBulk(Array.isArray(items));

      for (const item of itemList) {
        if (item?.api_sent_status === "success") continue;

        toggleLoading(item?.po_id, true);

        const body = {
          data: {
            po_id: item?.po_id,
            po_number: item?.po_number,
            po_batch_id: item?.po_batch_id,
            company_code: String(poFilters?.company?.code),
            company_name: poFilters?.company?.name,
          },
          event_type: "PO_SUBMIT_ADD",
          primary_key: item?.po_id,
        };

        try {
          await getPoGeneration(body);
        } catch (error) {
          console.error("API generation failed:", error);
        } finally {
          toggleLoading(item?.po_id, false);
        }
      }

      setIsBulk(false);
      fetchPoList();
    },
    [poFilters?.company, toggleLoading, fetchPoList, toast]
  );

  /** ----------------------------------------------
   * 🔹 File Download Utility
   * --------------------------------------------- */
  const downloadJson = (filename: string, jsonData: object) => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** ----------------------------------------------
   * 🔹 Derived Values (Memoized)
   * --------------------------------------------- */
  const formattedPoDate = useMemo(
    () => moment(poDetails?.[0]?.created_date).format("DD-MM-YYYY"),
    [poDetails]
  );

  /** ----------------------------------------------
   * 🔹 Render
   * --------------------------------------------- */
  return (
    <InvoiceLayout>
      <header className="flex justify-between items-center py-6 px-[3.75rem] shadow-md">
        <h1 className="text-[#49484C] font-bold text-[2rem]">Purchase Order</h1>
      </header>

      <div className="px-[5.75rem] pt-8">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate("/transactions/purchase-order")}
            className="bg-[#F1F1F1] border border-gray-300 px-4 py-2 text-sm text-[#49484C] hover:bg-gray-100"
          >
            Back to Search
          </Button>

          {poList.length > 1 && (
            <Button
              disabled={loadingPoIds?.size > 0}
              onClick={() => bulkUpload(poList)}
              className="bg-[#F1F1F1] border border-gray-300 px-4 py-2 text-sm text-[#49484C] hover:bg-gray-100"
            >
              {loadingPoIds?.size > 0 && isBulk ? (
                <>
                  <Spinner size="sm" /> Generating...
                </>
              ) : (
                "Generate All API"
              )}
            </Button>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 overflow-auto">
          {isFetchingPo ? (
            <div className="flex justify-center items-center h-[20rem]">
              <Spinner />
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100 text-[#616161] sticky top-0">
                <tr>
                  {[
                    "Sr. No.",
                    "Stockist Code",
                    "Stockist Name",
                    "PO Number",
                    "PO Date",
                    "API Status",
                    "API Response",
                    "SO Number",
                    "Generate Date",
                    "Actions",
                  ].map((header, i) => (
                    <th key={i} className="border px-2 py-2 whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {poList.map((item, index) => (
                  <tr key={item?.po_id ?? index} className="border-t hover:bg-gray-50">
                    <td className="border px-2 py-2">{index + 1}</td>
                    <td className="border px-2 py-2">{item?.compStockistCode}</td>
                    <td className="border px-2 py-2">{item?.stockist_name}</td>
                    <td className="border px-2 py-2">{item?.po_number}</td>
                    <td className="border px-2 py-2">
                      {moment(item?.created_date).format("DD-MM-YYYY")}
                    </td>
                    <td className="border px-2 py-2">{item?.api_sent_status}</td>
                    <td className="border px-2 py-2">
                      {item?.api_sent_status && (
                        <Button
                          onClick={() =>
                            setJsonModal({
                              show: true,
                              data: item?.api_message,
                              filename: `${item?.po_number}-Response-Payload`,
                            })
                          }
                          className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                        >
                          ⬇️ Download
                        </Button>
                      )}
                    </td>
                    <td className="border px-2 py-2">{item?.so_number}</td>
                    <td className="border px-2 py-2">{formattedPoDate}</td>
                    <td className="border px-2 py-2 flex gap-2">
                      <Button
                        onClick={() =>
                          navigate(`/transactions/purchase-order/${poBatchId}/${item?.po_id}`)
                        }
                        className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                      >
                        View Products
                      </Button>

                      {!item?.is_api_sent &&
                        item?.api_sent_status !== "success" &&
                        !disabledPoIds.has(item?.po_id) && (
                          <Button
                            disabled={loadingPoIds.has(item?.po_id)}
                            onClick={() => bulkUpload(item)}
                            className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                          >
                            {loadingPoIds?.has(item?.po_id) ? (
                              <>
                                <Spinner size="small" /> Generating...
                              </>
                            ) : (
                              "Generate API"
                            )}
                          </Button>
                        )}

                      {item?.api_request_paylaod && (
                        <Button
                          onClick={() =>
                            setJsonModal({
                              show: true,
                              data: item?.api_request_paylaod,
                              filename: `${item?.po_number}-Request-Payload`,
                            })
                          }
                          className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                        >
                          ⬇️ Payload
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* JSON Modal */}
      {jsonModal?.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md max-w-2xl w-full h-[80vh] flex flex-col">
            <h2 className="text-lg font-semibold mb-4">JSON Payload Preview</h2>
            <div className="flex-grow overflow-auto bg-gray-100 text-sm p-4 rounded">
              <pre>{JSON.stringify(JSON.parse(jsonModal?.data), null, 2)}</pre>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => setJsonModal({ show: false })}>Close</Button>
              <Button
                className="bg-violet-500 text-white hover:bg-violet-600"
                onClick={() => downloadJson(jsonModal?.filename!, jsonModal?.data)}
              >
                Download JSON
              </Button>
            </div>
          </div>
        </div>
      )}
    </InvoiceLayout>
  );
};

export default PoBulk;